import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import { useSyncedTab } from '../../../hooks/useSyncedTab';
import DetailLink from '../../Common/DetailLink';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../../../services/adminApi';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../Common/LoadingSpinner';
import PermissionGuard from '../../Common/PermissionGuard';
import LocationForm from './LocationForm';

const LocationDetails = () => {
  const { id } = useParams();
  const handleBack = useDetailBack('/admin/locations');
  const [location, setLocation] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useSyncedTab('details');
  const [showLocationForm, setShowLocationForm] = useState(false);

  useEffect(() => {
    fetchLocationDetails();
    fetchLocationUsers();
  }, [id]);

  const fetchLocationDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getLocationById(id);
      setLocation(response.data.data || response.data);
    } catch (error) {
      console.error('Error fetching location:', error);
      toast.error('Failed to load location details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationUsers = async () => {
    try {
      const response = await adminAPI.getLocationUsers(id);
      setUsers(response.data.data || response.data.users || []);
    } catch (error) {
      console.error('Error fetching location users:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"?`)) {
      return;
    }

    try {
      await adminAPI.deleteLocation(id);
      toast.success('Location deleted successfully');
      handleBack();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.message || 'Failed to delete location');
    }
  };

  const getLocationIcon = (type) => {
    switch (type?.toUpperCase()) {
      case 'WAREHOUSE':
        return <BuildingOfficeIcon className="w-8 h-8" />;
      case 'BRANCH':
        return <BuildingOfficeIcon className="w-8 h-8" />;
      default:
        return <MapPinIcon className="w-8 h-8" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'WAREHOUSE':
        return 'bg-blue-100 text-blue-800';
      case 'BRANCH':
        return 'bg-green-100 text-green-800';
      case 'OFFICE':
        return 'bg-purple-100 text-purple-800';
      case 'FACTORY':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading || !location) {
    return <LoadingSpinner message="Loading location details..." />;
  }

  const capacityPercentage = location.capacity?.totalSpace
    ? (location.capacity.usedSpace / location.capacity.totalSpace) * 100
    : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div>
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          Back to Locations
        </button>

        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 p-2 bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg text-indigo-600">
                {getLocationIcon(location.type)}
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{location.name}</h1>
                <p className="text-xs text-gray-500 mt-0.5 font-mono">{location.code}</p>
                <div className="flex items-center mt-2 space-x-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getTypeColor(location.type)}`}>
                    {location.type}
                  </span>
                  {location.isActive ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <CheckCircleIcon className="w-3 h-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                      <XCircleIcon className="w-3 h-3 mr-1" />
                      Inactive
                    </span>
                  )}
                  {location.isWarehouse && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                      Warehouse
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <PermissionGuard permission="ADMIN:LOCATIONS:UPDATE">
                <button
                  type="button"
                  onClick={() => setShowLocationForm(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-all"
                >
                  <PencilIcon className="w-3 h-3 mr-1.5" />
                  Edit
                </button>
              </PermissionGuard>
              <PermissionGuard permission="ADMIN:LOCATIONS:DELETE">
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-3 py-1.5 border border-red-300 rounded-lg shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-all"
                >
                  <TrashIcon className="w-3 h-3 mr-1.5" />
                  Delete
                </button>
              </PermissionGuard>
            </div>
          </div>

          {location.description && (
            <p className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">{location.description}</p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-t-lg">
        <nav className="flex space-x-6 px-4">
          <button
            onClick={() => setActiveTab('details')}
            className={`${
              activeTab === 'details'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`${
              activeTab === 'users'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
          >
            <UsersIcon className="w-4 h-4 mr-1.5" />
            Users
            <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-indigo-600 bg-indigo-100 rounded">
              {users.length}
            </span>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Address Information */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
              <MapPinIcon className="w-4 h-4 mr-2 text-indigo-600" />
              Address
            </h3>
            <dl className="space-y-2">
              <div>
                <dt className="text-xs font-semibold text-gray-500">Street</dt>
                <dd className="mt-0.5 text-sm text-gray-900">{location.address?.street || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-gray-500">City</dt>
                <dd className="mt-0.5 text-sm text-gray-900">{location.address?.city || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-gray-500">State</dt>
                <dd className="mt-0.5 text-sm text-gray-900">{location.address?.state || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-gray-500">Postal Code</dt>
                <dd className="mt-0.5 text-sm text-gray-900">{location.address?.postalCode || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-gray-500">Country</dt>
                <dd className="mt-0.5 text-sm text-gray-900">{location.address?.country || 'N/A'}</dd>
              </div>
              {location.coordinates?.latitude && location.coordinates?.longitude && (
                <div className="pt-2 border-t border-gray-200">
                  <dt className="text-xs font-semibold text-gray-500">GPS Coordinates</dt>
                  <dd className="mt-0.5 text-sm text-gray-900 font-mono">
                    {location.coordinates.latitude}, {location.coordinates.longitude}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Contact Information */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-4">
            <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
              <PhoneIcon className="w-4 h-4 mr-2 text-indigo-600" />
              Contact Information
            </h3>
            <dl className="space-y-2">
              <div>
                <dt className="text-xs font-semibold text-gray-500 flex items-center">
                  <PhoneIcon className="w-3 h-3 mr-1.5" />
                  Phone
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {location.contactInfo?.phone || 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Email
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {location.contactInfo?.email ? (
                    <a href={`mailto:${location.contactInfo.email}`} className="text-indigo-600 hover:text-indigo-900">
                      {location.contactInfo.email}
                    </a>
                  ) : 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center">
                  <GlobeAltIcon className="w-4 h-4 mr-2" />
                  Website
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {location.contactInfo?.website ? (
                    <a href={location.contactInfo.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900">
                      {location.contactInfo.website}
                    </a>
                  ) : 'N/A'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Warehouse Settings */}
          {location.isWarehouse && (
            <div className="bg-white shadow rounded-lg p-6 lg:col-span-2">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2 text-indigo-600" />
                Warehouse Capacity
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <dt className="text-sm font-medium text-gray-500">Total Space</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {location.capacity?.totalSpace?.toLocaleString() || 0}
                    <span className="text-xs text-gray-500 ml-1">
                      {location.capacity?.unit || 'sqft'}
                    </span>
                  </dd>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <dt className="text-sm font-medium text-gray-500">Used Space</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {location.capacity?.usedSpace?.toLocaleString() || 0}
                    <span className="text-xs text-gray-500 ml-1">
                      {location.capacity?.unit || 'sqft'}
                    </span>
                  </dd>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <dt className="text-sm font-medium text-gray-500">Available Space</dt>
                  <dd className="mt-1 text-2xl font-semibold text-gray-900">
                    {((location.capacity?.totalSpace || 0) - (location.capacity?.usedSpace || 0)).toLocaleString()}
                    <span className="text-xs text-gray-500 ml-1">
                      {location.capacity?.unit || 'sqft'}
                    </span>
                  </dd>
                </div>
              </div>

              {/* Capacity Bar */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Capacity Usage</span>
                  <span className="text-sm font-medium text-gray-700">{capacityPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className={`h-4 rounded-full transition-all ${
                      capacityPercentage > 90 ? 'bg-red-600' :
                      capacityPercentage > 75 ? 'bg-yellow-600' :
                      'bg-green-600'
                    }`}
                    style={{ width: `${Math.min(capacityPercentage, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Users Assigned to this Location
              </h3>
              <PermissionGuard permission="ADMIN:LOCATIONS:ASSIGN">
                <DetailLink
                  to={`/admin/locations/${id}/assign-users`}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <UsersIcon className="w-4 h-4 mr-2" />
                  Assign Users
                </DetailLink>
              </PermissionGuard>
            </div>
          </div>
          
          {users.length === 0 ? (
            <div className="text-center py-12">
              <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users assigned</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by assigning users to this location.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((userLocation) => {
                    const user = userLocation.userId || {};
                    const role = userLocation.locationRole || userLocation.role || 'location_user';
                    const isActive = userLocation.isActive ?? userLocation.status === 'active';

                    return (
                    <tr key={userLocation._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name || 'Unknown User'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {String(role).replace(/^location_/, '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {userLocation.accessLevel || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isActive ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {userLocation.status || 'Inactive'}
                          </span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showLocationForm && location && (
        <LocationForm
          location={location}
          onSuccess={() => {
            setShowLocationForm(false);
            fetchLocationDetails();
          }}
          onCancel={() => setShowLocationForm(false)}
        />
      )}
    </div>
  );
};

export default LocationDetails;
